import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchJobs } from '@/lib/api';
import type { JobWithRelations } from '@/lib/api';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface JobRun {
  id: string;
  job_id: string;
  status: string;
  phase: string | null;
  started_at: string;
  completed_at: string | null;
  records_scraped: number | null;
  urls_discovered: number | null;
  urls_scraped: number | null;
  error_message: string | null;
  metadata: {
    total_urls?: number;
    phase?: string;
    current_url?: string;
    urls_discovered?: number;
    urls_scraped?: number;
  } | null;
}

export const useJobRealtime = () => {
  const [jobs, setJobs] = useState<JobWithRelations[]>([]);
  const [activeRuns, setActiveRuns] = useState<Map<string, JobRun>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const loadJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchJobs();
      setJobs(data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load active runs (including paused)
  const loadActiveRuns = useCallback(async () => {
    const { data } = await (supabase as any)
      .from('job_runs')
      .select('*')
      .in('status', ['running'])
      .order('started_at', { ascending: false });
    
    if (data) {
      const runsMap = new Map<string, JobRun>();
      data.forEach((run: JobRun) => {
        // Only keep the most recent run per job
        if (!runsMap.has(run.job_id)) {
          runsMap.set(run.job_id, run);
        }
      });
      setActiveRuns(runsMap);
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadActiveRuns();

    // Subscribe to job_runs changes
    const runsChannel = supabase
      .channel('job_runs_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_runs',
        },
        (payload: RealtimePostgresChangesPayload<JobRun>) => {
          console.log('Job run update:', payload);
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const run = payload.new as JobRun;
            setActiveRuns(prev => {
              const next = new Map(prev);
              if (run.status === 'running') {
                next.set(run.job_id, run);
              } else {
                next.delete(run.job_id);
              }
              return next;
            });

            // Refresh jobs list when a run completes
            if (run.status === 'completed' || run.status === 'failed') {
              loadJobs();
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Job runs realtime subscription status:', status);
      });

    // Subscribe to scrape_jobs changes
    const jobsChannel = supabase
      .channel('scrape_jobs_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scrape_jobs',
        },
        () => {
          console.log('Jobs list updated');
          loadJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(runsChannel);
      supabase.removeChannel(jobsChannel);
    };
  }, [loadJobs, loadActiveRuns]);

  // Merge active run status into jobs
  const jobsWithStatus = jobs.map(job => {
    const activeRun = activeRuns.get(job.id);
    if (activeRun) {
      return {
        ...job,
        status: 'running' as const,
        _activeRun: activeRun,
      };
    }
    return job;
  });

  return {
    jobs: jobsWithStatus,
    activeRuns,
    isLoading,
    refetch: loadJobs,
  };
};
